/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  type ApiTaskSettingsModifiers,
  DEFAULT_DEFERRED_TASK_MODIFIER,
  DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER,
  DEFERRED_TASK_MODIFIER_KEY,
  ExportsDataSchema,
  ScanEditorShowTagsKebabAndMenuSchema,
  TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY,
} from '@kaiizen/kai-pro-api';
import { getEnabledTaskModifiers, useAllOrgSettings, useFeatureFlags, useOrgSettings } from '@kaiizen/kai-pro-api-react-state';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useToast } from '@/Client/State';
import {
  SettingsHeaderRow,
  SettingsInsightBanner,
  SettingsIntro,
  SettingsPanel,
} from '@/Client/Widgets';

import { type SettingsRecord, useSettingsAutosave } from './useSettingsAutosave';

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

type OutputsDraftState = {
  includeTaskPhotos: boolean;
  includePageNumbers: boolean;
  showDeferredTasks: boolean;
  showTenantResponsibleTasks: boolean;
  logoSvg: string | null;
};

/**
 * The tab's settings as one autosave record, keyed by field.
 *
 * They persist through up to three writes across two mutators - the `exports` and `scanEditor`
 * org settings and the whole-settings modifiers record - so they share one record and one save
 * chain rather than three writers racing.
 *
 * Every save sends all of it, as Save Changes did, with two qualifications. The `scanEditor` and
 * modifiers writes go only while the modifier settings flag is off, since the Modifiers page owns
 * them once it is on. And the logo key is always in the payload, but what it carries depends on
 * which field is being saved: a save of the logo itself always carries what was picked, while a
 * save of any other key carries the logo only while the merge base still holds one - see the
 * payload comment in `save`. When the logo should be sent, and what a page may assume the server
 * holds for it, is KAIDEV-4362's question. */
type OutputsDraftValue = OutputsDraftState[keyof OutputsDraftState];

type OutputsDraftRecord = SettingsRecord<OutputsDraftValue>;

const OUTPUTS_DRAFT_KEYS = [
  'includeTaskPhotos',
  'includePageNumbers',
  'showDeferredTasks',
  'showTenantResponsibleTasks',
  'logoSvg',
] as const satisfies readonly (keyof OutputsDraftState)[];

const getOutputsDraftRecord = (state: OutputsDraftState): OutputsDraftRecord => Object.fromEntries(OUTPUTS_DRAFT_KEYS.map(key => [key, state[key]]));

const getOutputsDraftState = (record: OutputsDraftRecord): OutputsDraftState => ({
  includeTaskPhotos: record.includeTaskPhotos !== false,
  includePageNumbers: record.includePageNumbers !== false,
  showDeferredTasks: record.showDeferredTasks === true,
  showTenantResponsibleTasks: record.showTenantResponsibleTasks === true,
  logoSvg: typeof record.logoSvg === 'string' ? record.logoSvg : null,
});

type ModifierRecord = Record<string, ApiTaskSettingsModifiers>;

const DEFAULT_DRAFT_STATE: OutputsDraftState = {
  includeTaskPhotos: true,
  includePageNumbers: true,
  showDeferredTasks: false,
  showTenantResponsibleTasks: false,
  logoSvg: null,
};

const getDraftStateFromOrgSettings = (
  exportsSettings: any,
  scanEditorSettings: any,
  modifiers: ModifierRecord | undefined,
): OutputsDraftState => {
  const exportsData = exportsSettings?.data
    ? ExportsDataSchema.parse(exportsSettings.data)
    : undefined;
  const scanEditorData = scanEditorSettings?.data
    ? ScanEditorShowTagsKebabAndMenuSchema.parse(scanEditorSettings.data)
    : undefined;
  const logoSvg = exportsSettings?.logoSvg ?? null;
  const deprecatedShowLineItemTagging = scanEditorData?.showLineItemTagsKebabAndMenu?.value ?? false;
  const modernModifiersConfigured = modifiers !== undefined;
  // Read enablement through the same resolver the scope editor uses, rather than
  // treating key presence alone as "on". This keeps the Outputs toggle in lockstep
  // with the kebab menu: a modifier stranded with `isEnabled: false` (key present
  // but explicitly disabled) correctly reads as off here, instead of the toggle
  // saying on while the editor shows no item.
  const enabledModifierKeys = new Set(getEnabledTaskModifiers(modifiers).map(({ key }) => key));

  return {
    includeTaskPhotos: exportsData?.includeTaskPhotos?.value ?? true,
    includePageNumbers: exportsData?.includePageNumbers?.value ?? true,
    showDeferredTasks: modernModifiersConfigured
      ? enabledModifierKeys.has(DEFERRED_TASK_MODIFIER_KEY)
      : deprecatedShowLineItemTagging,
    showTenantResponsibleTasks: modernModifiersConfigured
      ? enabledModifierKeys.has(TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY)
      : deprecatedShowLineItemTagging,
    logoSvg,
  };
};

const getOutputScopeEditorModifiers = (
  existingModifiers: ModifierRecord | undefined,
  draft: Pick<
    OutputsDraftState,
    'showDeferredTasks' | 'showTenantResponsibleTasks'
  >,
): ModifierRecord => {
  const {
    [DEFERRED_TASK_MODIFIER_KEY]: existingDeferredModifier,
    [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: existingTenantResponsibilityModifier,
    ...otherModifiers
  } = existingModifiers ?? {};

  const nextModifiers: ModifierRecord = { ...otherModifiers };

  // Stamp `isEnabled: true` when a toggle is on so turning it on always re-enables:
  // without this, flipping on a modifier stranded with `isEnabled: false` would
  // preserve the flag and silently re-read as off. Turning a toggle off drops the key.
  if (draft.showDeferredTasks) {
    nextModifiers[DEFERRED_TASK_MODIFIER_KEY] = {
      ...(existingDeferredModifier ?? DEFAULT_DEFERRED_TASK_MODIFIER),
      isEnabled: true,
    };
  }

  if (draft.showTenantResponsibleTasks) {
    nextModifiers[TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY] = {
      ...(existingTenantResponsibilityModifier ?? DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER),
      isEnabled: true,
    };
  }

  return nextModifiers;
};

/**
 * Org Settings → Outputs (KAIDEV-4345).
 *
 * Autosaves, as the rest of the surface now does: no save control, no cancel and no navigation
 * guard. Removing a logo is the one thing that does not take: the server reads a cleared logo as
 * "no change", so Remove says so and leaves it in place until KAIDEV-4362 adds the signal that
 * removes it.
 */
export const OutputsTabContent = () => {
  const { orgSettingsByKey, orgSettingsMutators, isLoading } = useOrgSettings();
  const {
    allOrgSettings,
    allOrgSettingsMutators,
    isLoading: isAllOrgSettingsLoading,
  } = useAllOrgSettings();
  const { showToast, showErrorToast } = useToast();
  const { featureFlags } = useFeatureFlags();
  const modifierSettingsEnabled = !!featureFlags.modifierSettings;
  const fileInputRef = useRef<HTMLInputElement>(null);
  /* The file the reader chose, kept with its contents so the name can be shown only while the
     draft still holds what they picked. A failed save reverts the field, and a name left over it
     claimed an upload that had not happened. It is not a setting - nothing stores it - so it
     stays out of the autosave record. */
  const [pickedLogo, setPickedLogo] = useState<{ name: string; svg: string }>();

  const exportsSettings = orgSettingsByKey?.exports;
  const scanEditorSettings = orgSettingsByKey?.scanEditor;
  const persistedModifiers = allOrgSettings?.taskSettings?.modifiers as
    | ModifierRecord
    | undefined;

  const persisted = useMemo(
    () =>
      getOutputsDraftRecord(
        getDraftStateFromOrgSettings(
          exportsSettings,
          scanEditorSettings,
          persistedModifiers,
        ),
      ),
    [exportsSettings, persistedModifiers, scanEditorSettings],
  );

  const isSettingsLoading = isLoading || isAllOrgSettingsLoading;

  const save = useCallback(async (record: OutputsDraftRecord) => {
    const state = getOutputsDraftState(record);
    /* `allSettled`, not `all`: these are separate writes, and the first rejection used to abandon
       the batch mid-flight with no record of which parts the server had taken (KAIDEV-4345
       review). The edited field is still reverted whole, so a failure in one part can still
       revert a field whose own part succeeded - only reachable with the modifier settings flag
       off, the one state that sends more than one write, and it goes when that path does. */
    try {
      const outcomes = await Promise.allSettled([
        orgSettingsMutators.update('exports', {
          data: {
            includeTaskPhotos: { value: state.includeTaskPhotos },
            includePageNumbers: { value: state.includePageNumbers },
          },
          /* Always present, and a null means "no change" to the server: `629ff6430` (2026-02-19)
             turned `!== undefined` into `!= null`, which is why "Remove Uploaded Logo" has removed
             nothing since, and KAIDEV-4362 keeps it meaning that on purpose, signalling removal
             with its own `clearLogoSvg` flag precisely because every deployed build sends a null
             here and always will.

             What it carries depends on which field is being saved, and that is worth stating
             plainly rather than claiming parity with `development`, which sends the draft's logo
             every time. `runSave` builds the payload as the merge base plus the edited key, so a
             save OF the logo always carries what was picked. A save of any other key takes the
             logo from the merge base - and `toStoredForm` drops this key, so after any successful
             save there is no logo there until the settings echo re-seeds it, and a toggle that
             chains before that echo sends a null. Benign, because the server reads a null as "no
             change", but intermittent - and KAIDEV-4362 inherits it along with the send rule, so
             it is recorded there rather than left to be rediscovered (review rounds 11 and 12). */
          logoSvg: state.logoSvg,
        }),
        /* Once the Modifiers page owns modifiers, this tab must not write them. Its draft knows
           only "on" and "off", and off deletes the key - so saving an unrelated setting here
           would delete a modifier an admin had merely disabled on that page, taking its label
           with it. The legacy scanEditor flag is written from the same two toggles, so it goes
           the same way. */
        ...(modifierSettingsEnabled ? [] : [
          orgSettingsMutators.update('scanEditor', {
            data: {
              showLineItemTagsKebabAndMenu: {
                value: state.showDeferredTasks || state.showTenantResponsibleTasks,
              },
            },
          }),
          allOrgSettingsMutators.update({
            taskSettings: {
              modifiers: getOutputScopeEditorModifiers(persistedModifiers, state),
            },
          }),
        ]),
      ]);
      const failure = outcomes.find((outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected');
      if (failure) throw failure.reason;
    } catch (error) {
      showErrorToast(error, 'save output settings');
      throw error;
    }
  }, [
    allOrgSettingsMutators,
    modifierSettingsEnabled,
    orgSettingsMutators,
    persistedModifiers,
    showErrorToast,
  ]);

  /* Every other field round-trips as sent. The logo never does, whatever this page sends: a null
     is read as "no change" and leaves whatever is stored in place, and a non-null one can be
     overtaken by another admin's upload between the save and its echo. The page cannot know, so
     it does not say - the key is dropped, no confirmation is held for it, and the next settings
     refresh re-seeds the logo from the server.

     Naming one is what pinned the guard four rounds running: a null the server ignores (round 7),
     the snapshot a removal cleared (round 8), an upload that was never sent (round 9), and the
     pick that outlived it (round 10). Each fix moved the window rather than closing it, because
     the page was answering a question only the server can.

     What it does not do is re-seed on its own: a refresh landing while a save is outstanding is
     skipped for this key, so after a removal the field can sit on "No File Selected" until the
     next one. Accepted - the press already says removal is not available, and KAIDEV-4362
     rewrites this path when it makes removal real. */
  const toStoredForm = useCallback(
    (record: OutputsDraftRecord): OutputsDraftRecord => {
      const stored = { ...record };
      delete stored.logoSvg;
      return stored;
    },
    [],
  );

  const { draft: draftRecord, updateEntry } = useSettingsAutosave<OutputsDraftValue>(
    persisted,
    { save, toStoredForm },
  );

  const draft = useMemo(() => getOutputsDraftState(draftRecord), [draftRecord]);

  // Every control here is a toggle or a file choice - a decision already made - so each saves at
  // once rather than waiting out a pause.
  const setDraftValue = <Key extends keyof OutputsDraftState>(
    key: Key,
    value: OutputsDraftState[Key],
  ) => updateEntry(key, value, { immediate: true });

  const logoFileName = (pickedLogo && draft.logoSvg === pickedLogo.svg ? pickedLogo.name : undefined)
    ?? (draft.logoSvg ? 'Current Logo Uploaded' : 'No File Selected');

  const handleBrowseLogo = () => {
    fileInputRef.current?.click();
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'image/svg+xml') {
      showToast('Please select an SVG file', 'error');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = loadEvent => {
      const svgContent = loadEvent.target?.result;
      if (typeof svgContent !== 'string') {
        showToast('Failed to read logo file', 'error');
        return;
      }

      setPickedLogo({ name: file.name, svg: svgContent });
      setDraftValue('logoSvg', svgContent);
    };
    reader.onerror = () => {
      showToast('Failed to read logo file', 'error');
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleRemoveLogo = () => {
    setPickedLogo(undefined);
    setDraftValue('logoSvg', null);
    /* Said out loud rather than left to be discovered from a PDF. The server keeps the logo on a
       null, so the field comes back as soon as the save settles; KAIDEV-4362 adds the signal that
       actually removes it. */
    showToast('Removing a logo is not available yet - it will still appear on documents.', 'warning');
  };

  const artifactRows = [
    {
      title: 'Include Task Photos',
      body: 'Task photos will be included in exported scope PDFs.',
      enabled: draft.includeTaskPhotos,
      disabled: isSettingsLoading,
      onToggleChange: (enabled: boolean) => setDraftValue('includeTaskPhotos', enabled),
    },
    {
      title: 'Include Page Numbers',
      body: 'Page numbers will be included in exported scope PDFs.',
      enabled: draft.includePageNumbers,
      disabled: isSettingsLoading,
      onToggleChange: (enabled: boolean) => setDraftValue('includePageNumbers', enabled),
    },
  ] as const;

  /* These titles are fixed on purpose and do not follow the org's modifier display names. The
     toggles render only while the `modifierSettings` flag is off; with it on, the Modifiers page
     replaces them and is where modifiers are renamed. */
  const scopeEditorRows = [
    {
      title: 'Deferred Tasks',
      body: 'Allow users to mark items as deferred in the scope of work editor.',
      enabled: draft.showDeferredTasks,
      disabled: isSettingsLoading,
      onToggleChange: (enabled: boolean) => setDraftValue('showDeferredTasks', enabled),
    },
    {
      title: 'Resident Responsible Tasks',
      body: 'Allow users to mark items as resident responsible in the scope of work editor.',
      enabled: draft.showTenantResponsibleTasks,
      disabled: isSettingsLoading,
      onToggleChange: (enabled: boolean) => setDraftValue('showTenantResponsibleTasks', enabled),
    },
  ] as const;

  const logoRows = [
    {
      type: 'fileUpload' as const,
      fileName: logoFileName,
      actionLabel: draft.logoSvg ? 'Replace' : 'Browse',
      onActionClick: handleBrowseLogo,
      actionDisabled: isSettingsLoading,
      secondaryActionLabel: draft.logoSvg ? 'Remove Uploaded Logo' : undefined,
      onSecondaryActionClick: draft.logoSvg ? handleRemoveLogo : undefined,
      secondaryActionDisabled: isSettingsLoading,
    },
  ] as const;

  return (
    <div css={CONTENT_STYLE}>
      <SettingsInsightBanner
        title='Output Settings Insight'
        body='Configure exported artifact content, scope-editor tagging visibility, and the company logo used in generated PDFs.'
        dismissCookieKey='org-settings-outputs-insight-dismissed'
      />
      {
        /* No save control: every change here is already stored. */
      }
      <SettingsHeaderRow
        title='Artifact Settings'
        counts={{ lead: '5 settings' }}
        hideDefaultActions
      />
      <SettingsIntro
        title='Artifact Content'
        body='Choose what to include in exported scope PDFs.'
      />
      <SettingsPanel title='Task Photos & Pagination' rows={artifactRows} />
      {
        /* The Modifiers page replaces these two toggles with per-modifier configuration, so the
          flag swaps one surface for the other rather than showing both (KAIDEV-3182). */
      }
      {!modifierSettingsEnabled && (
        <>
          <SettingsIntro
            title='Scope Editor'
            body='Control whether modifier tagging is available in the scope of work editor.'
          />
          <SettingsPanel title='Tagging' rows={scopeEditorRows} />
        </>
      )}
      <SettingsIntro
        title='Company Logo'
        body='Upload an SVG logo to include in exported artifact PDFs.'
      />
      <SettingsPanel title='Upload Logo' rows={logoRows} />

      <input
        ref={fileInputRef}
        type='file'
        accept='.svg,image/svg+xml'
        onChange={handleLogoFileChange}
        hidden
      />
    </div>
  );
};
